import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Paper, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import logo from '../assets/logo.png';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    const clean = email.toLowerCase().trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setError('Please enter a valid email address.');
      return;
    }

    setWorking(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Something went wrong.');
      // The server answers identically whether or not the account exists, so
      // there's nothing here that reveals which addresses are registered.
      setSent(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
      <Box component="img" src={logo} alt="Rewarding" sx={{ height: 34, mb: 4 }} />
      <Paper elevation={0} sx={{ width: '100%', maxWidth: 420, p: { xs: 2.5, sm: 4 }, borderRadius: 4, border: '1px solid #eee' }}>
        {sent ? (
          <Box sx={{ textAlign: 'center' }}>
            <MarkEmailReadIcon sx={{ fontSize: 44, color: '#4caf50', mb: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 1 }}>
              Check your email
            </Typography>
            <Typography variant="body2" sx={{ color: '#777', mb: 3 }}>
              If that email has an account, we've sent a link to reset your password.
              It expires in an hour and can only be used once.
            </Typography>
            <Button fullWidth variant="contained" onClick={() => navigate('/login')}
              sx={{ textTransform: 'none', fontWeight: 700, py: 1.4, minHeight: 48, borderRadius: 2.5, bgcolor: '#1a1a2e', '&:hover': { bgcolor: '#2a2a4e' } }}>
              Back to sign in
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={submit}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 0.5 }}>
              Forgot your password?
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              Enter your email and we'll send you a link to reset it.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            <TextField
              fullWidth
              type="email"
              label="Email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              autoComplete="email"
              autoFocus
              sx={{ mb: 2.5 }}
            />

            <Button fullWidth type="submit" variant="contained" disabled={working}
              sx={{ textTransform: 'none', fontWeight: 700, py: 1.4, minHeight: 48, borderRadius: 2.5, bgcolor: '#1a1a2e', '&:hover': { bgcolor: '#2a2a4e' } }}>
              {working ? 'Sending…' : 'Send reset link'}
            </Button>

            <Button fullWidth startIcon={<ArrowBackIcon />} onClick={() => navigate('/login')}
              sx={{ textTransform: 'none', color: '#999', mt: 1.5 }}>
              Back to sign in
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
